import { listen, qs, add, remove } from "@fluorescent/dom";
import { on } from "@/glow/events";

import { updateFilters } from "@/lib/filters/events";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import updateInnerHTML from "@/lib/update-inner-html";
import ItemLayoutToggle from "@/lib/item-layout-toggle";
import debounce from "@/utils/debounce";
import getMediaQuery from "@/lib/media-queries";

const selectors = {
  topbarForm: "[data-filter-topbar]",
  resultsContainer: "[data-results-container]",
  filterItem: "[data-filter-item]",
  sidebarToggle: "[data-sidebar-toggle]",
  drawerToggle: "[data-drawer-toggle]",
  sortToggle: "[data-topbar-sort-toggle]",
  sortValues: "[data-topbar-sort-values]",
  sortGroup: "[data-topbar-sort-group]",
  groupLabels: "[data-filter-group-label]",
  groupValues: "[data-filter-group-values]",
  filterInputs: "[data-filter-item-input]",
  sortInputs: "[data-sort-item-input]",
  sortLabel: "[data-sort-label]",
  rangeInput: "[data-range-input]",
  resultsCount: "[data-results-count]",
  resultsCountMobile:
    ".filter-topbar__results-count-wrapper [data-results-count]",
  activeFilters: "[data-active-filters]",
  sortWash: "[data-topbar-sort-wash]",
};

const classes = {
  filterDisabled: "filter-item__content--disabled",
  filterGroupActive: "filter-group--active",
};

// eslint-disable-next-line valid-jsdoc
/**
 * A class to handle desktop filter bar functionality
 * @param {*} node the collection section container
 * @returns renderFilters and unload methods
 */
const filterBar = node => {
  // `node` is the collection section container.
  // Using `container` here as the filter bar container to keep filter bar
  // and filter drawer DOM scope separate.
  const container = node ? qs(selectors.topbarForm, node) : null;
  if (!node || !container) return;

  const itemLayoutToggle = ItemLayoutToggle(node);
  const sortToggleEl = qs(selectors.sortToggle, container);
  const sortValuesEl = qs(selectors.sortValues, container);
  const sortGroupEl = qs(selectors.sortGroup, container);

  let focusTrap = null;

  const events = [
    listen(container, "change", changeHandler),
    listen(container, "keydown", ({ keyCode }) => {
      if (keyCode === 27) hideSort();
    }),
  ];

  if (sortToggleEl) {
    events.push(listen(window, "click", clickHandler));

    // Hide sort if the header account link is clicked
    on("header-account-link:clicked", hideSort);
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Delegates click events
   * @param {event} e click event
   */
  function clickHandler(e) {
    const targetGroupEl = e.target.closest(selectors.sortGroup);
    const targetSortToggleEl = e.target.closest(selectors.sortToggle);
    const sortWashEl = e.target.closest(selectors.sortWash);

    // If the click happened outside of a filter group
    // We don't want to close the groups if the click happened on a filter in a group
    if (!targetGroupEl || sortWashEl) {
      hideSort();
    }

    if (targetSortToggleEl) {
      toggleSort();
    }
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Delegates change events
   * @param {event} e change event
   */
  function changeHandler(e) {
    const sortInput = e.target.closest(
      `${selectors.topbarForm} ${selectors.sortInputs}`
    );

    if (sortInput) {
      filterChange(sortInput);
    }
  }

  function toggleSort() {
    const ariaExpanded = sortToggleEl.getAttribute("aria-expanded") === "true";

    if (ariaExpanded) {
      hideSort();
    } else {
      showSort();
    }
  }

  function showSort() {
    updateScrollLock(true);
    sortToggleEl.setAttribute("aria-expanded", true);
    sortValuesEl.setAttribute("aria-hidden", false);
    add(sortGroupEl, classes.filterGroupActive);
  }

  function hideSort() {
    updateScrollLock(false);
    sortToggleEl.setAttribute("aria-expanded", false);
    sortValuesEl.setAttribute("aria-hidden", true);
    remove(sortGroupEl, classes.filterGroupActive);
  }

  function updateScrollLock(addScrollLock) {
    if (window.matchMedia(getMediaQuery("below-720")).matches) {
      if (addScrollLock) {
        disableBodyScroll(sortValuesEl, {
          allowTouchMove: el => {
            while (el && el !== document.body) {
              if (el.getAttribute("data-scroll-lock-ignore") !== null) {
                return true;
              }
              el = el.parentNode;
            }
          },
        });
      } else {
        enableBodyScroll(sortValuesEl);
      }
    }
  }

  function filterChange(filter) {
    if (filter.classList.contains(classes.filterDisabled)) {
      return;
    }

    debounce(() => updateFilters(container), 300)();
  }

  function renderFilters(doc) {
    updateInnerHTML(`${selectors.topbarForm} ${selectors.sidebarToggle}`, doc);
    updateInnerHTML(`${selectors.topbarForm} ${selectors.drawerToggle}`, doc);
    updateInnerHTML(`${selectors.topbarForm} ${selectors.resultsCount}`, doc);
    updateInnerHTML(`${selectors.topbarForm} ${selectors.sortLabel}`, doc);
    updateInnerHTML(`${selectors.topbarForm} ${selectors.activeFilters}`, doc);

    updateInnerHTML(
      `${selectors.topbarForm} ${selectors.resultsCountMobile}`,
      doc
    );

    updateInnerHTML(
      `${selectors.resultsContainer} ${selectors.activeFilters}`,
      doc
    );
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    focusTrap?.deactivate();
    itemLayoutToggle && itemLayoutToggle.unload();
  }

  return {
    renderFilters,
    unload,
  };
};

export default filterBar;
